import { useRef, useState } from 'react'
import { TriangleAlert } from 'lucide-react'
import { SendLaterButton } from 'app/components/sms-scheduling/SendLaterButton'
import { SmsTemplatePicker } from 'app/components/sms-templates/TemplatePicker'
import { Button } from 'app/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'app/components/ui/dialog'
import { Input } from 'app/components/ui/input'
import { extractTokenKeys } from 'app/lib/messageTemplates'
import { readFallbacks, writeFallbacks } from 'app/lib/workflows/messageFallbacks'
import { OPT_OUT_LINE } from 'app/lib/smsOptOut'
import { smsSegments } from 'app/lib/smsSegments'
import { BulkProgressBar } from './BulkProgressBar'
import type { BulkProgress, MergeVariable, SmsValue } from './types'

type BulkSendSmsModalProps = {
  open: boolean
  count: number
  applying: boolean
  /** Live send progress, present only while a send is running. */
  progress?: BulkProgress | null
  variables?: MergeVariable[]
  /**
   * Selected rows whose practice is inside quiet hours (8pm-8am) right now.
   * When > 0 the warning banner gains a quiet-hours sentence; otherwise quiet
   * hours are never mentioned (they'd be noise outside the window).
   */
  quietSkipCount?: number
  /**
   * A practice zone to draw the picker's presets and times against. The zone
   * every selected row shares when they share one; for a selection spanning
   * practices, the first selected row's zone stands in rather than the picker
   * being withheld. The caller resolves each recipient's own zone at apply time
   * (see `useContactBulkActions`), so the value collected here is a wall clock
   * that reaches every practice at the same LOCAL hour, and a contact whose own
   * clock rejects it is skipped and reported.
   */
  timezone?: string
  /** Whether the schedule control may be offered at all (feature toggle plus a
   *  non-empty selection, decided by the caller). */
  scheduleAvailable?: boolean
  onApply: (value: SmsValue) => void
  onCancel: () => void
}

/** True if the variable's token (or a legacy alias of it) appears in the message. */
function isUsed(text: string, variable: MergeVariable): boolean {
  return extractTokenKeys(text).includes(variable.key)
}

/**
 * Modal for the bulk "Send SMS" action. Collects one message body (with optional
 * merge tokens filled per contact at send time) plus a fallback for each token in
 * use. Sending is a real outward action.
 */
export function BulkSendSmsModal({
  open,
  count,
  applying,
  progress,
  variables,
  quietSkipCount = 0,
  timezone,
  scheduleAvailable,
  onApply,
  onCancel,
}: BulkSendSmsModalProps) {
  const [text, setText] = useState(``)
  const [fallbacks, setFallbacks] = useState<Record<string, string>>({})
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const trimmed = text.trim()
  const usedVariables = (variables ?? []).filter((v) => isUsed(text, v))
  const hasTokens = extractTokenKeys(text).length > 0
  const { units, segments, encoding } = smsSegments(text)

  const insertToken = (token: string) => {
    const el = textareaRef.current
    const start = el?.selectionStart ?? text.length
    const end = el?.selectionEnd ?? text.length
    const next = text.slice(0, start) + token + text.slice(end)
    setText(next)
    // Restore focus + place the cursor just after the inserted token.
    requestAnimationFrame(() => {
      if (!el) return
      el.focus()
      const pos = start + token.length
      el.setSelectionRange(pos, pos)
    })
  }

  // Dismissing mid-send would hide the progress while the loop keeps texting people,
  // so the dialog holds (matching the disabled Cancel button) until the run ends.
  return (
    <Dialog open={open} onOpenChange={(next) => !next && !applying && onCancel()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Send SMS to {count} {count === 1 ? `contact` : `contacts`}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            This sends a real text message to each selected contact. Contacts with no cell phone or an opted-out number
            are skipped. You'll see a full breakdown in the results.
            {quietSkipCount > 0 &&
              (scheduleAvailable
                ? ` It's quiet hours (8pm-8am) for ${
                    quietSkipCount === count ? `all ${count}` : `${quietSkipCount} of the selected contacts`
                  } right now. Use the clock to schedule it instead, and it goes out at the time you pick, on each practice's own clock.`
                : ` ${
                    quietSkipCount === count
                      ? `It's quiet hours (8pm-8am) at every selected practice right now, so nothing will send.`
                      : `It's quiet hours (8pm-8am) for ${quietSkipCount} of the selected contacts right now; they will be skipped.`
                  }`)}
          </span>
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <label className="text-ui-sm font-medium text-default">Message</label>
            <SmsTemplatePicker
              trigger={
                <button type="button" className="text-xs text-primary-600 hover:underline">
                  Use template
                </button>
              }
              currentBody={text}
              onSelect={(t) => {
                // The template's fallbacks live inside its body; lift them into
                // the per-send inputs so editing an input changes what sends.
                insertToken(writeFallbacks(t.body, {}))
                // Only the empty inputs; anything the user typed wins (spec).
                setFallbacks((prev) => {
                  const next = { ...prev }
                  for (const [key, text] of Object.entries(readFallbacks(t.body))) {
                    if (!next[key]?.trim()) next[key] = text
                  }
                  return next
                })
              }}
            />
          </div>
          <textarea
            ref={textareaRef}
            autoFocus
            value={text}
            rows={4}
            placeholder="Type your message…"
            onChange={(e) => setText(e.target.value)}
            className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
          />
          {!!variables?.length && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-subtle">Insert:</span>
              {variables.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  onClick={() => insertToken(v.token)}
                  className="rounded-full border border-default bg-surface-subtle px-2 py-0.5 text-xs text-default hover:bg-surface-muted"
                >
                  {v.label}
                </button>
              ))}
            </div>
          )}
          <div className="text-xs text-subtle">
            {hasTokens ? `~` : ``}
            {units} chars · {segments} {segments === 1 ? `segment` : `segments`}
            {encoding === `UCS-2` ? ` · uses emoji/special characters` : ``} per recipient
          </div>
          <p className="text-xs text-minimal">
            First-time recipients texted from a CallRail number get “{OPT_OUT_LINE}” added by CallRail automatically
            when the message has no opt-out wording.
          </p>
        </div>

        {usedVariables.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <label className="text-ui-sm font-medium text-default">
              Fallbacks <span className="font-normal text-minimal">(used when a contact is missing the value)</span>
            </label>
            {usedVariables.map((v) => (
              <div key={v.key} className="flex items-center gap-2">
                <span className="w-28 shrink-0 text-xs text-subtle">{v.label}</span>
                <Input
                  value={fallbacks[v.key] ?? ``}
                  placeholder="e.g. there"
                  onChange={(e) => setFallbacks((prev) => ({ ...prev, [v.key]: e.target.value }))}
                  className="h-8 flex-1"
                />
              </div>
            ))}
          </div>
        )}

        {progress && <BulkProgressBar progress={progress} verb="Sending" />}

        <DialogFooter>
          {scheduleAvailable && (
            <SendLaterButton
              timezone={timezone}
              disabled={!trimmed || applying}
              onSchedule={(sendAtLocal) => onApply({ text, fallbacks, sendAtLocal })}
            />
          )}
          <Button variant="white" onClick={onCancel} disabled={applying}>
            Cancel
          </Button>
          <Button disabled={!trimmed || applying} onClick={() => onApply({ text, fallbacks })}>
            {applying ? `Sending…` : `Send to ${count}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
